import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../axios/axiosInstance";

/**
 * Live-checks a phone number against the backend while the user types.
 * Returns one of:
 *   "idle"           — empty input, no message
 *   "invalid"        — too short or missing +
 *   "invalid_format" — doesn't match E.164
 *   "checking"       — API call in flight
 *   "available"      — number is free
 *   "taken"          — number belongs to another account
 *
 * @param {string} phoneNumber  Full E.164 number (e.g. "+14155551234")
 * @param {string} [userId]     Exclude this user from the check (self)
 */
export const usePhoneAvailability = (phoneNumber, userId) => {
  const [status, setStatus] = useState("idle");
  const debounceRef = useRef(null);

  useEffect(() => {
    const value = (phoneNumber || "").trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value) {
      setStatus("idle");
      return;
    }

    // Quick client-side validation
    if (!value.startsWith("+") || value.length < 9) {
      setStatus("invalid");
      return;
    }

    if (!/^\+\d{8,15}$/.test(value)) {
      setStatus("invalid_format");
      return;
    }

    setStatus("checking");
    let cancelled = false;

    debounceRef.current = setTimeout(async () => {
      try {
        const params = { phoneNumber: value };
        if (userId) params.userId = userId;

        const { data } = await axiosInstance.get("/user/check-phone", {
          params,
          skipAuthLogout: true,
        });
        if (cancelled) return;

        if (data?.available) {
          setStatus("available");
        } else {
          setStatus(data?.reason || "taken");
        }
      } catch {
        if (!cancelled) setStatus("idle");
      }
    }, 450);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [phoneNumber, userId]);

  return status;
};
